import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, ListPlus, Rotate3d, Trash2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { DRBFMSession } from '../../../types';
import { canResumeSession } from './sessionNavigation';
import { selectDesignVerificationEnabled } from '@features/featureFlags/featureFlagsSlice';
import { useAppSelector } from '@store/index';

interface SessionRowActionsProps {
  session: DRBFMSession;
  isLoadingView3D: boolean;
  isExporting: boolean;
  isConvertBusy: boolean;
  onView: (session: DRBFMSession) => void;
  onView3D: (session: DRBFMSession) => void;
  onExport: (session: DRBFMSession) => void;
  onConvert: (session: DRBFMSession) => void;
  onDelete: (session: DRBFMSession) => void;
}

/**
 * The row itself navigates, so every action stops the click from bubbling.
 * Disabled buttons intentionally keep their tooltip silent: a disabled control
 * fires no pointer events, which is exactly what MUI did here.
 */
const SessionRowActions: React.FC<SessionRowActionsProps> = ({
  session,
  isLoadingView3D,
  isExporting,
  isConvertBusy,
  onView,
  onView3D,
  onExport,
  onConvert,
  onDelete,
}) => {
  const { t } = useTranslation();
  const designVerificationEnabled = useAppSelector(selectDesignVerificationEnabled);

  const act = (handler: (session: DRBFMSession) => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    handler(session);
  };

  return (
    <div className="flex justify-end gap-1">
      {canResumeSession(session) && (
        <TooltipIconButton
          size="icon-sm"
          label={t('drbfm.history.resume', '編集を再開')}
          icon={<Eye strokeWidth={1.9} />}
          onClick={act(onView)}
        />
      )}
      {session.status === 'completed' && (
        <>
          <TooltipIconButton
            size="icon-sm"
            className="text-primary hover:text-primary"
            label={t('drbfm.history.view3D', '3Dで見る')}
            icon={isLoadingView3D ? <Spinner size="sm" label="" /> : <Rotate3d strokeWidth={1.9} />}
            disabled={isLoadingView3D}
            onClick={act(onView3D)}
          />
          <TooltipIconButton
            size="icon-sm"
            className="text-primary hover:text-primary"
            label={t('drbfm.history.exportExcel', 'Excel出力')}
            icon={isExporting ? <Spinner size="sm" label="" /> : <Download strokeWidth={1.9} />}
            disabled={isExporting}
            onClick={act(onExport)}
          />
          {designVerificationEnabled !== false && (
            <TooltipIconButton
              size="icon-sm"
              className="text-success hover:text-success"
              label={t('drbfm.convert.buttonLabel', '検証項目に変換')}
              icon={<ListPlus strokeWidth={1.9} />}
              disabled={isConvertBusy}
              onClick={act(onConvert)}
            />
          )}
        </>
      )}
      <TooltipIconButton
        size="icon-sm"
        className="text-destructive hover:text-destructive"
        label={t('drbfm.history.delete', '削除')}
        icon={<Trash2 strokeWidth={1.9} />}
        onClick={act(onDelete)}
      />
    </div>
  );
};

export default SessionRowActions;
